import { z } from "zod";
import type { CreateReferenceRequirementDto } from "@fp/admin-api-client/types/createReferenceRequirementDto";
import type { ReferenceRequirementDto } from "@fp/admin-api-client/types/referenceRequirementDto";
import { compileReferenceFormatMask, validateReferenceFormatMask } from "./reference-format-mask";

export const MAX_FORMATS = 10;

export const MAX_REFERENCE_REQUIREMENTS = 10;

export const referenceRequirementSchema = z.object({
	label: z
		.string()
		.trim()
		.min(1, "Label is required")
		.max(100, "Label cannot be longer than 100 characters"),
	masks: z
		.array(
			z.object({
				value: z.string().superRefine((mask, ctx) => {
					const error = validateReferenceFormatMask(mask);
					if (error) {
						ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
					}
				}),
			}),
		)
		.max(MAX_FORMATS, `A reference cannot have more than ${MAX_FORMATS} formats`),
	hint: z.string().trim().max(255, "Hint cannot be longer than 255 characters"),
	example: z.string().trim().max(100, "Example cannot be longer than 100 characters"),
})
	.superRefine((requirement, ctx) => {
		const seen = new Set<string>();
		requirement.masks.forEach((mask, index) => {
			// Matching is case-insensitive, so formats differing only by case accept the same values
			const key = mask.value.trim().toLowerCase();
			if (!key) {
				return;
			}
			if (seen.has(key)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "This format has already been added",
					path: ["masks", index, "value"],
				});
			}
			seen.add(key);
		});

		const example = requirement.example.trim();
		const compiled = requirement.masks
			.map((mask) => compileReferenceFormatMask(mask.value.trim()))
			.filter((pattern) => pattern !== null);

		if (example && compiled.length > 0 && !compiled.some((pattern) => pattern.test(example))) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Example does not match any of the formats",
				path: ["example"],
			});
		}
	});

export type ReferenceRequirementSchema = z.infer<typeof referenceRequirementSchema>;

export const referenceRequirementsSchema = z
	.array(referenceRequirementSchema)
	.max(MAX_REFERENCE_REQUIREMENTS, `An opportunity cannot have more than ${MAX_REFERENCE_REQUIREMENTS} reference requirements`)
	.superRefine((requirements, ctx) => {
		const seen = new Set<string>();
		requirements.forEach((requirement, index) => {
			const key = requirement.label.trim().toLowerCase();
			if (!key) {
				return;
			}
			if (seen.has(key)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Labels must be unique",
					path: [index, "label"],
				});
			}
			seen.add(key);
		});
	});

export const toReferenceRequirementFormValues = (
	requirements: ReferenceRequirementDto[],
): ReferenceRequirementSchema[] =>
	requirements.map((requirement) => ({
		label: requirement.label,
		masks: requirement.masks.map((value) => ({ value })),
		hint: requirement.hint ?? "",
		example: requirement.example ?? "",
	}));

export const toCreateReferenceRequirementDtos = (
	requirements: ReferenceRequirementSchema[],
): CreateReferenceRequirementDto[] =>
	requirements.map((requirement) => ({
		label: requirement.label.trim(),
		masks: requirement.masks.map((mask) => mask.value.trim()),
		hint: requirement.hint.trim() || null,
		example: requirement.example.trim() || null,
	}));
